import { useContext } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { apiPost, apiGet } from 'api'
import AuthContext from 'context/auth-context'

// Enqueue one agentic turn. Returns the conversation ({ id, status, events, agent_actions }); the
// server executes the work in the background and streams progress into the conversation's events,
// which the client polls via useAssistantConversation.
export function useSendAssistantMessage({ reportId, options = {} }) {
  const token = useContext(AuthContext)

  return useMutation(
    ({ message, conversationId, selectedChart, selectedEmptySlot }) =>
      apiPost({
        url: `reports/${reportId}/assistant`,
        token,
        objectName: 'assistant',
        params: {
          message,
          conversation_id: conversationId ?? undefined,
          selected_chart: selectedChart ?? undefined,
          selected_empty_slot: selectedEmptySlot ?? undefined
        }
      }),
    { ...options }
  )
}

// Merge a delta response onto the events already cached, ignoring any the cache already holds so a
// racing pair of polls sharing a cursor can't double-render an event.
const mergeEvents = (cached, incoming) => {
  const merged = [...cached]
  const seen = new Set(merged.map((event) => event.id))
  incoming.forEach((event) => {
    if (!seen.has(event.id)) {
      seen.add(event.id)
      merged.push(event)
    }
  })
  return merged
}

// Poll a conversation's live activity feed while a turn is running.
//
// The poll is incremental: it sends the last event it holds as a cursor and the server returns only
// newer ones, which are stitched onto the cache here. A document build emits hundreds of events over
// a turn polled about once a second, so refetching the whole timeline each time grows quadratically.
export function useAssistantConversation({ reportId, conversationId, options = {} }) {
  const token = useContext(AuthContext)
  const queryClient = useQueryClient()
  const key = [`reports/${reportId}/assistant/conversations/${conversationId}`]

  return useQuery(
    key,
    async () => {
      const cached = queryClient.getQueryData(key)
      const cachedEvents = cached?.events ?? []
      const data = await apiGet({
        url: `reports/${reportId}/assistant/conversations/${conversationId}`,
        token,
        params: { after_event_id: cachedEvents[cachedEvents.length - 1]?.id }
      })
      return { ...data, events: mergeEvents(cachedEvents, data.events ?? []) }
    },
    { enabled: !!reportId && !!conversationId, ...options }
  )
}

// List the report's conversations (most-recent first) for the history dropdown.
export function useAssistantConversations({ reportId, options = {} }) {
  const token = useContext(AuthContext)

  return useQuery(
    [`reports/${reportId}/assistant/conversations`],
    () => apiGet({ url: `reports/${reportId}/assistant/conversations`, token }),
    { enabled: !!reportId, ...options }
  )
}

// Ask a running turn to stop. The server flags the conversation and the worker ends at its next
// tool-use round, so this returns before the turn is actually over — the poll reports when it is.
export function useCancelAssistantTurn({ reportId, options = {} }) {
  const token = useContext(AuthContext)

  return useMutation(
    (conversationId) =>
      apiPost({ url: `reports/${reportId}/assistant/conversations/${conversationId}/cancel`, token }),
    { ...options }
  )
}

// Reverse a single agent action from the activity feed.
export function useUndoAgentAction({ options = {} }) {
  const token = useContext(AuthContext)

  return useMutation(
    (agentActionId) => apiPost({ url: `agent_actions/${agentActionId}/undo`, token }),
    { ...options }
  )
}
